"use client";

import { useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { resolvePublicAssetUrl } from "@/lib/api";

interface ProductGalleryImage {
  id: string;
  filePath: string;
  altText?: string | null;
  fileName?: string;
}

export function ProductGallery({ images }: { images: ProductGalleryImage[] }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  if (images.length === 0) {
    return null;
  }

  const active = images[activeIndex];
  const activeSrc = resolvePublicAssetUrl(active.filePath);

  return (
    <>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_120px]">
        <button
          type="button"
          onClick={() => setLightboxOpen(true)}
          className="group relative overflow-hidden rounded-3xl border border-border bg-card"
        >
          <img src={activeSrc} alt={active.altText || active.fileName || "Product screenshot"} className="h-[420px] w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" loading="lazy" />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-6 py-5 text-left text-sm font-medium text-white">
            Click to zoom
          </div>
        </button>

        <div className="grid grid-cols-4 gap-3 lg:grid-cols-1 lg:overflow-y-auto">
          {images.map((image, index) => {
            const imageSrc = resolvePublicAssetUrl(image.filePath);
            const isActive = index === activeIndex;
            return (
              <button
                key={image.id}
                type="button"
                onClick={() => setActiveIndex(index)}
                className={`overflow-hidden rounded-2xl border transition ${isActive ? "border-primary ring-2 ring-primary/20" : "border-border hover:border-primary/40"}`}
              >
                <img src={imageSrc} alt={image.altText || image.fileName || "Product screenshot"} className="h-24 w-full object-cover" loading="lazy" />
              </button>
            );
          })}
        </div>
      </div>

      {lightboxOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <button type="button" className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20" onClick={() => setLightboxOpen(false)}>
            <X className="h-5 w-5" />
          </button>
          {images.length > 1 ? (
            <>
              <Button type="button" variant="outline" size="icon" className="absolute left-4 top-1/2 -translate-y-1/2 border-white/20 bg-black/30 text-white hover:bg-black/50" onClick={() => setActiveIndex((current) => (current === 0 ? images.length - 1 : current - 1))}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button type="button" variant="outline" size="icon" className="absolute right-4 top-1/2 -translate-y-1/2 border-white/20 bg-black/30 text-white hover:bg-black/50" onClick={() => setActiveIndex((current) => (current === images.length - 1 ? 0 : current + 1))}>
                <ChevronRight className="h-5 w-5" />
              </Button>
            </>
          ) : null}
          <img src={activeSrc} alt={active.altText || active.fileName || "Product screenshot"} className="max-h-[88vh] max-w-[92vw] rounded-3xl object-contain" />
        </div>
      ) : null}
    </>
  );
}